import React from "react";
import { T } from "../../app/tokens.js";
import { activeFilterGroups } from "../../utils/filterHiddenSearch.js";
import { LIST_PRESENTATION } from "./listPresentation.js";

const STYLES = {
  toolbar: { display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap", minWidth: 0 },
  controls: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", flex: "1 1 360px", minWidth: 0 },
  trailing: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, flexWrap: "wrap", marginLeft: "auto", minHeight: T.toolbarRowH, maxWidth: "100%" },
  count: { color: T.collectionCount, fontSize: T.fsSm, whiteSpace: "nowrap", flexShrink: 0 },
  countLine: { display: "block", textAlign: "left", marginTop: 4, lineHeight: "16px" },
  chip: {
    display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 7px",
    border: "1px solid var(--v2-blue-border)", borderRadius: 999,
    color: T.listAccent, background: T.listChipBg, fontSize: T.fsSm,
    fontWeight: T.fwSemibold, maxWidth: "100%", overflowWrap: "anywhere",
  },
  remove: { background: "none", border: "none", cursor: "pointer", padding: 0, color: T.listAccent, fontSize: T.fsSm, lineHeight: 1, fontWeight: T.fwBold, flexShrink: 0 },
};

export function InlineFilterClear({ testId, onClick }) {
  return <button type="button" data-testid={testId} onClick={onClick}
    onMouseEnter={(e) => { e.currentTarget.style.background = T.blueSoft; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    style={{ ...LIST_PRESENTATION.clear, padding: "7px 0", whiteSpace: "nowrap" }}>Clear</button>;
}

// Presentation only. Callers retain their eligibility, reset and authorization
// contracts. Only the requested list lenses opt into the below-controls count.
// Their old empty hint anchor contributed two 12px gaps: the 20px count slot
// plus the remaining 4px gap reuses that space without moving the data header.
export function CollectionCount({ testId, count, total, below = true }) {
  return <span data-testid={testId} style={{ ...STYLES.count, ...(below ? STYLES.countLine : {}) }}>
    {`Displaying ${count} of ${total}`}
  </span>;
}

export function InlineFilterToolbar({ testId, leftTestId, busy, countTestId, count, total, trailing, children, countBelow = false, reuseCountGap = false, afterCount, collectionActions = false }) {
  const countNode = <CollectionCount testId={countTestId} count={count} total={total} below={countBelow} />;
  const toolbar = (
    <div data-testid={testId} style={{ ...STYLES.toolbar, ...(collectionActions ? { minHeight: T.toolbarRowH } : {}) }}>
      <div data-testid={leftTestId} aria-busy={busy} style={STYLES.controls}>
        {children}
        {!countBelow && countNode}
      </div>
      {/* Keep the row/count footprint, but center actions on the actual 33px
          controls rather than the 40px row. Compact keeps its own 20px height. */}
      <div data-testid={`${testId}-trailing`} style={{ ...STYLES.trailing, ...(collectionActions ? { minHeight: T.toolbarCtrlH } : {}) }}>
        {trailing}
      </div>
    </div>
  );
  if (!countBelow) return toolbar;
  return <div data-testid={`${testId}-composition`} style={{ minWidth: 0, ...(reuseCountGap ? { marginBottom: -8 } : {}) }}>
    {toolbar}
    {countNode}
    {afterCount}
  </div>;
}

export function InlineFilterChips({ prefix, chips }) {
  return chips.map(({ id, label, onRemove, removeLabel, testId, removeTestId, role, ariaLabel }) => (
    <span key={id} role={role} aria-label={ariaLabel} data-inline-filter-chip="" data-testid={testId ?? `${prefix}-${id}-chip`} style={STYLES.chip}>
      {label}
      {onRemove && <button type="button" data-testid={removeTestId ?? `${prefix}-${id}-chip-remove`}
        aria-label={removeLabel ?? `Remove ${id} filter`} onClick={onRemove} style={STYLES.remove}>×</button>}
    </span>
  ));
}

// One chip per changed facet, preserving OR selections within that facet.
// Checked-inclusion facets restore their own baseline (or all options), not
// an empty array: empty means match-none on these lists.
export function facetChips({ dimensions, selections, baseline = {}, checked = false, onChange }) {
  return activeFilterGroups(dimensions, selections, baseline, checked).map(({ id, label, options }) => {
    const selected = selections[id] ?? [];
    const values = selected.map((value) => options.find((option) => option.value === value)?.label ?? value);
    return {
      id,
      label: `${label}: ${checked && selected.length === options.length ? "All" : values.join(", ") || (checked ? "None" : "All")}`,
      removeLabel: `Remove ${label} filter`,
      onRemove: () => onChange({
        ...selections,
        [id]: baseline[id] ?? (checked ? options.map(({ value }) => value) : []),
      }),
    };
  });
}
